import React, { useState } from 'react';
import { Alert, Button, Group, Paper, Select, Stack, Text, Textarea, ThemeIcon, Title } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { IconFileSpreadsheet, IconInfoCircle, IconTrash, IconUpload, IconX } from '@tabler/icons-react';
import { SMART_IMPORT_TYPES, acceptedFile, formatFileSize, importTypeName } from '@/services/import-draft/import-draft-helpers';

export interface UploadState {
  importType: number;
  file: File | null;
  instruction: string;
  allowGeneratedCodes: boolean;
}

interface Props {
  state: UploadState;
  onChange: (next: UploadState) => void;
  onSubmit: (file: File) => void;
  submitting: boolean;
  error?: string | null;
}

export default function UploadStep({ state, onChange, onSubmit, submitting, error }: Props) {
  const [showNote, setShowNote] = useState(!!state.instruction);
  const fileProblem = state.file ? acceptedFile(state.file) : null;

  function takeFile(file: File | null) {
    onChange({ ...state, file });
  }

  return (
    <Stack gap="md" data-testid="upload-step">
      <div>
        <Title order={4}>Import a file</Title>
        <Text size="sm" c="dimmed">
          Drop a CSV, Excel or PDF file. The columns are matched for you, and nothing is saved until you confirm.
        </Text>
      </div>

      <Select
        label="File contents"
        data={SMART_IMPORT_TYPES.map(t => ({ value: String(t), label: importTypeName(t) === 'Inventory' ? 'Inventory items' : 'Customers' }))}
        value={String(state.importType)}
        onChange={value => value && onChange({ ...state, importType: Number(value) })}
        allowDeselect={false}
        disabled={submitting}
        w={260}
      />

      {state.file ? (
        <Paper withBorder radius="md" p="md" data-testid="selected-file">
          <Group justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap" miw={0}>
              <ThemeIcon variant="light" size="lg" radius="md"><IconFileSpreadsheet size={20} /></ThemeIcon>
              <div style={{ minWidth: 0 }}>
                <Text size="sm" fw={500} truncate="end" title={state.file.name}>{state.file.name}</Text>
                <Text size="xs" c="dimmed">{formatFileSize(state.file.size)}</Text>
              </div>
            </Group>
            <Button
              variant="subtle"
              color="red"
              size="xs"
              leftSection={<IconTrash size={14} />}
              onClick={() => onChange({ ...state, file: null })}
              disabled={submitting}
              data-testid="remove-file"
            >
              Remove
            </Button>
          </Group>
        </Paper>
      ) : (
        <Dropzone
          onDrop={files => takeFile(files[0] || null)}
          multiple={false}
          maxSize={25 * 1024 * 1024}
          accept={[
            'text/csv', 'text/plain', 'text/tab-separated-values', 'application/pdf', 'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel.sheet.macroEnabled.12',
          ]}
          disabled={submitting}
          data-testid="upload-dropzone"
        >
          <Group justify="center" gap="md" mih={120} style={{ pointerEvents: 'none' }}>
            <Dropzone.Accept><IconUpload size={36} /></Dropzone.Accept>
            <Dropzone.Reject><IconX size={36} /></Dropzone.Reject>
            <Dropzone.Idle><IconFileSpreadsheet size={36} /></Dropzone.Idle>
            <div>
              <Text size="md">Drop a file here, or click to browse.</Text>
              <Text size="xs" c="dimmed">CSV, Excel, including .xls, and text PDFs are supported. Maximum size 25 MB.</Text>
            </div>
          </Group>
        </Dropzone>
      )}
      {fileProblem && <Text size="sm" c="red">{fileProblem}</Text>}

      <div>
        <Button variant="subtle" size="xs" onClick={() => setShowNote(v => !v)} disabled={submitting}>
          {showNote ? 'Hide note' : 'Add a note about this file'}
        </Button>
        {showNote && (
          <Textarea
            mt="xs"
            label="Note"
            description="Only if the file is unusual. For example, which column is the company name, or that prices include VAT."
            value={state.instruction}
            onChange={e => onChange({ ...state, instruction: e.currentTarget.value })}
            autosize
            minRows={2}
            maxLength={2000}
            disabled={submitting}
          />
        )}
      </div>

      {error && <Alert color="red" icon={<IconInfoCircle size={16} />}>{error}</Alert>}

      {state.file && !fileProblem && (
        <Group justify="flex-end">
          <Button onClick={() => onSubmit(state.file!)} loading={submitting} data-testid="upload-next">
            Next
          </Button>
        </Group>
      )}
    </Stack>
  );
}
